import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Building2, Plus, Edit, Mail, RefreshCcw, FileText, Upload, ScrollText,
  Layers, KeyRound, Trash2, Eye, EyeOff, Copy, Wand2, ShieldAlert, FileDown,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Normalize an axios error into a renderable string. FastAPI 422 responses put
// an ARRAY of objects in `detail`; passing that straight to toast/JSX crashes
// React ("Objects are not valid as a React child") and white-pages the app.
const errMsg = (err, fallback = 'Something went wrong') => {
  const d = err?.response?.data?.detail;
  if (Array.isArray(d)) {
    const s = d.map((x) => (typeof x === 'string' ? x : x?.msg)).filter(Boolean).join(', ');
    return s || fallback;
  }
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object') return d.msg || fallback;
  return fallback;
};

// ─────────────────────────────────────────────────────────────────────────
// MPCA Division Portal — Admin landing page
//
// Phase 1+2 surfaces:
//   - Divisions tab    : create / edit MPCA divisions, resend portal invite
//   - Grant Policies   : publish the corpus-fund scheme that divisions claim
//                        against. Doc upload is wired to Emergent Object Storage.
//
// Phase 3+ (Division Portal, Claims pipeline, AI review) will mount under
// a separate `/portal` route — left out of this page deliberately so the
// admin pageweight stays tight.
// ─────────────────────────────────────────────────────────────────────────

const EMPTY_DIVISION = {
  name: '', code: '', district: '',
  contact_email: '', contact_phone: '', secretary_name: '',
  password: '',
};

const EMPTY_POLICY = {
  fiscal_year: '', title: '', description: '', eligible_amount: '',
  required_documents: 'claim_letter, invoice, bank_proof',
  status: 'active',
};

const Divisions = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('divisions');

  // Divisions state
  const [divisions, setDivisions] = useState([]);
  const [loadingDivisions, setLoadingDivisions] = useState(true);
  const [divDialogOpen, setDivDialogOpen] = useState(false);
  const [divEditId, setDivEditId] = useState(null);
  const [divResetPw, setDivResetPw] = useState(null); // { id, code, password }
  const [divResetSubmitting, setDivResetSubmitting] = useState(false);
  // Once the password is saved, we surface it on-screen with a copy button
  // so the admin can hand it to the secretary right then. The plaintext is
  // never re-fetchable — bcrypt — so this is the only chance to see it.
  const [divResetReveal, setDivResetReveal] = useState(null);
  const [divResetVisible, setDivResetVisible] = useState(false);
  const [divDelete, setDivDelete] = useState(null); // { id, code, name, confirm }
  const [divDeleteSubmitting, setDivDeleteSubmitting] = useState(false);
  const [divForm, setDivForm] = useState({ ...EMPTY_DIVISION });
  const [divInviteResult, setDivInviteResult] = useState(null);
  const [divSubmitting, setDivSubmitting] = useState(false);

  // Policies state
  const [policies, setPolicies] = useState([]);
  const [loadingPolicies, setLoadingPolicies] = useState(true);
  const [polDialogOpen, setPolDialogOpen] = useState(false);
  const [polEditId, setPolEditId] = useState(null);
  const [polForm, setPolForm] = useState({ ...EMPTY_POLICY });
  const [polSubmitting, setPolSubmitting] = useState(false);
  const [policyFileUploading, setPolicyFileUploading] = useState(null);

  useEffect(() => { fetchDivisions(); fetchPolicies(); }, []);

  const fetchDivisions = async () => {
    setLoadingDivisions(true);
    try {
      const res = await axios.get(`${API}/divisions`, { withCredentials: true });
      setDivisions(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(errMsg(e, 'Failed to load divisions'));
    } finally {
      setLoadingDivisions(false);
    }
  };

  const fetchPolicies = async () => {
    setLoadingPolicies(true);
    try {
      const res = await axios.get(`${API}/grant-policies`, { withCredentials: true });
      setPolicies(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(errMsg(e, 'Failed to load policies'));
    } finally {
      setLoadingPolicies(false);
    }
  };

  // ─── Division handlers ──────────────────────────────────────────────────
  const openDivCreate = () => {
    setDivEditId(null);
    setDivForm({ ...EMPTY_DIVISION });
    setDivInviteResult(null);
    setDivDialogOpen(true);
  };

  const openDivEdit = (d) => {
    setDivEditId(d.id);
    setDivForm({
      name: d.name || '', code: d.code || '', district: d.district || '',
      contact_email: d.contact_email || '', contact_phone: d.contact_phone || '',
      secretary_name: d.secretary_name || '',
    });
    setDivInviteResult(null);
    setDivDialogOpen(true);
  };

  const submitDivision = async (e) => {
    e.preventDefault();
    setDivSubmitting(true);
    try {
      if (divEditId) {
        const { code, ...payload } = divForm; // eslint-disable-line no-unused-vars
        await axios.put(`${API}/divisions/${divEditId}`, payload, { withCredentials: true });
        toast.success('Division updated');
        setDivDialogOpen(false);
      } else {
        // Omit a blank password so the backend treats it as "no password /
        // Google OAuth" instead of failing the min_length(6) validation.
        const createPayload = { ...divForm };
        if (!createPayload.password) delete createPayload.password;
        const res = await axios.post(`${API}/divisions`, createPayload, { withCredentials: true });
        setDivInviteResult(res.data);
        toast.success('Division created — share portal URL with the secretary manually');
      }
      fetchDivisions();
    } catch (err) {
      toast.error(errMsg(err, 'Save failed'));
    } finally {
      setDivSubmitting(false);
    }
  };

  const resendInvite = async (divisionId) => {
    try {
      const res = await axios.post(`${API}/divisions/${divisionId}/resend-invite`, {}, { withCredentials: true });
      if (res.data.email_sent) {
        toast.success('Welcome email re-sent');
      } else {
        try {
          await navigator.clipboard.writeText(res.data.portal_url);
          toast.info('Email failed — portal URL copied to clipboard');
        } catch {
          toast.info(`Portal URL: ${res.data.portal_url}`);
        }
      }
    } catch (e) {
      toast.error(errMsg(e, 'Failed to resend'));
    }
  };

  // ─── Policy handlers ────────────────────────────────────────────────────
  const openPolicyCreate = () => {
    setPolEditId(null);
    setPolForm({ ...EMPTY_POLICY });
    setPolDialogOpen(true);
  };

  const openPolicyEdit = (p) => {
    setPolEditId(p.id);
    setPolForm({
      fiscal_year: p.fiscal_year || '',
      title: p.title || '',
      description: p.description || '',
      eligible_amount: p.eligible_amount ?? '',
      required_documents: (p.required_documents || []).join(', '),
      status: p.status || 'active',
    });
    setPolDialogOpen(true);
  };

  const submitPolicy = async (e) => {
    e.preventDefault();
    setPolSubmitting(true);
    const payload = {
      fiscal_year: polForm.fiscal_year,
      title: polForm.title,
      description: polForm.description,
      eligible_amount: Number(polForm.eligible_amount) || 0,
      required_documents: polForm.required_documents
        .split(',').map(s => s.trim()).filter(Boolean),
      status: polForm.status,
    };
    try {
      if (polEditId) {
        await axios.put(`${API}/grant-policies/${polEditId}`, payload, { withCredentials: true });
        toast.success('Policy updated');
      } else {
        await axios.post(`${API}/grant-policies`, payload, { withCredentials: true });
        toast.success('Policy created');
      }
      setPolDialogOpen(false);
      fetchPolicies();
    } catch (err) {
      toast.error(errMsg(err, 'Save failed'));
    } finally {
      setPolSubmitting(false);
    }
  };

  const togglePolicyStatus = async (p) => {
    try {
      await axios.put(
        `${API}/grant-policies/${p.id}`,
        { status: p.status === 'active' ? 'closed' : 'active' },
        { withCredentials: true },
      );
      fetchPolicies();
    } catch (e) {
      toast.error(errMsg(e, 'Failed'));
    }
  };

  const uploadPolicyDoc = async (policyId, file) => {
    if (!file) return;
    setPolicyFileUploading(policyId);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/grant-policies/${policyId}/document`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Policy document uploaded');
      fetchPolicies();
    } catch (e) {
      toast.error(errMsg(e, 'Upload failed'));
    } finally {
      setPolicyFileUploading(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="divisions-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold flex items-center gap-3">
            <Building2 className="h-7 w-7 text-accent" />
            MPCA Division Portal
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage MPCA divisions and the grant policies they can claim against.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground hidden sm:inline">Secretary guide:</span>
          <Button
            variant="outline" size="sm" asChild
            data-testid="download-secretary-guide-en"
            title="Download English walkthrough PDF for division secretaries"
          >
            <a href={`${API}/guides/secretary?lang=en&format=pdf`} target="_blank" rel="noreferrer">
              <FileDown className="h-4 w-4 mr-1.5" />
              English PDF
            </a>
          </Button>
          <Button
            variant="outline" size="sm" asChild
            data-testid="download-secretary-guide-hi"
            title="हिन्दी मार्गदर्शिका PDF डाउनलोड करें"
          >
            <a href={`${API}/guides/secretary?lang=hi&format=pdf`} target="_blank" rel="noreferrer">
              <FileDown className="h-4 w-4 mr-1.5" />
              हिन्दी PDF
            </a>
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="divisions" data-testid="tab-divisions">
            Divisions <Badge variant="secondary" className="ml-2">{divisions.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="policies" data-testid="tab-policies">
            Grant Policies <Badge variant="secondary" className="ml-2">{policies.length}</Badge>
          </TabsTrigger>
        </TabsList>

        {/* ───────── Divisions tab ───────── */}
        <TabsContent value="divisions" className="space-y-4">
          <Card>
            <CardHeader className="border-b border-border/50">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <CardTitle>Divisions</CardTitle>
                  <CardDescription>
                    Each division gets a portal-only login. The secretary signs in with Google using the contact email — no password is set.
                  </CardDescription>
                </div>
                <Button onClick={openDivCreate} data-testid="add-division-button">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Division
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              {loadingDivisions ? (
                <div className="text-sm text-muted-foreground">Loading…</div>
              ) : divisions.length === 0 ? (
                <div className="text-sm text-muted-foreground py-6 text-center">
                  No divisions yet. Click <em>Add Division</em> to onboard the first one.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>District</TableHead>
                      <TableHead>Secretary</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {divisions.map((d) => (
                      <TableRow key={d.id} data-testid={`division-row-${d.code}`}>
                        <TableCell className="font-medium">{d.name}</TableCell>
                        <TableCell><Badge variant="outline" className="font-mono text-xs">{d.code}</Badge></TableCell>
                        <TableCell>{d.district || '—'}</TableCell>
                        <TableCell>{d.secretary_name}</TableCell>
                        <TableCell>
                          <div className="text-sm">{d.contact_email}</div>
                          <div className="text-xs text-muted-foreground">{d.contact_phone || ''}</div>
                        </TableCell>
                        <TableCell>
                          {d.is_active ? (
                            <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Active</Badge>
                          ) : (
                            <Badge variant="secondary">Inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button size="sm" variant="ghost" onClick={() => openDivEdit(d)} data-testid={`edit-div-${d.code}`}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => { setDivResetPw({ id: d.id, code: d.code, password: '' }); setDivResetReveal(null); setDivResetVisible(false); }}
                            title="Set / reset secretary password"
                            data-testid={`reset-pw-${d.code}`}
                          >
                            <KeyRound className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => resendInvite(d.id)} title="Resend portal invite">
                            <RefreshCcw className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => setDivDelete({ id: d.id, code: d.code, name: d.name, confirm: '' })}
                            title="Permanently delete this division"
                            data-testid={`delete-div-${d.code}`}
                            className="hover:bg-rose-50"
                          >
                            <Trash2 className="h-4 w-4 text-rose-600" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ───────── Policies tab ───────── */}
        <TabsContent value="policies" className="space-y-4">
          <Card>
            <CardHeader className="border-b border-border/50">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <CardTitle>Grant Policies</CardTitle>
                  <CardDescription>
                    The scheme(s) all active divisions can submit claims against. Toggle status to make a policy visible or invisible to the portal.
                  </CardDescription>
                </div>
                <Button onClick={openPolicyCreate} data-testid="add-policy-button">
                  <Plus className="h-4 w-4 mr-2" />
                  New Policy
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              {loadingPolicies ? (
                <div className="text-sm text-muted-foreground">Loading…</div>
              ) : policies.length === 0 ? (
                <div className="text-sm text-muted-foreground py-6 text-center">
                  No policies yet. Create one so divisions can begin submitting claims.
                </div>
              ) : (
                <div className="space-y-3">
                  {policies.map((p) => (
                    <div
                      key={p.id}
                      className="rounded-lg border bg-card p-4 space-y-2 hover:border-emerald-400 hover:shadow-sm transition cursor-pointer"
                      data-testid={`policy-card-${p.id}`}
                      onClick={() => navigate(`/grant-policies/${p.id}`)}
                      role="button"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold">{p.title}</h3>
                            <Badge variant="outline" className="text-xs">{p.fiscal_year}</Badge>
                            {p.status === 'active' ? (
                              <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Active</Badge>
                            ) : (
                              <Badge variant="secondary">Closed</Badge>
                            )}
                          </div>
                          {p.description && (
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{p.description}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span><strong className="text-foreground">₹{(p.eligible_amount || 0).toLocaleString('en-IN')}</strong> per division</span>
                            <span>{(p.required_documents || []).length} required doc(s)</span>
                            {p.policy_document?.filename && (
                              <a
                                href={`${API}/grant-policies/${p.id}/document/download`}
                                target="_blank" rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="flex items-center gap-1 underline-offset-4 hover:underline text-foreground"
                              >
                                <FileText className="h-3 w-3" />
                                {p.policy_document.filename}
                              </a>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/grant-claims?policy=${p.id}`)}
                            data-testid={`view-claims-${p.id}`}
                            className="mr-2"
                          >
                            <Layers className="h-4 w-4 mr-1.5" />
                            View Claims
                          </Button>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mr-2">
                            <span>{p.status === 'active' ? 'Active' : 'Closed'}</span>
                            <Switch
                              checked={p.status === 'active'}
                              onCheckedChange={() => togglePolicyStatus(p)}
                              data-testid={`toggle-policy-${p.id}`}
                            />
                          </div>
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => navigate(`/grant-policies/${p.id}`)}
                            title="Edit details"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <label className="cursor-pointer">
                            <Button
                              size="sm" variant="ghost" asChild
                              disabled={policyFileUploading === p.id}
                            >
                              <span>
                                <Upload className={`h-4 w-4 ${policyFileUploading === p.id ? 'animate-pulse' : ''}`} />
                              </span>
                            </Button>
                            <input
                              type="file"
                              accept="application/pdf,image/*"
                              className="hidden"
                              onChange={(e) => uploadPolicyDoc(p.id, e.target.files?.[0])}
                            />
                          </label>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ───────── Division Create/Edit Dialog ───────── */}
      <Dialog open={divDialogOpen} onOpenChange={(o) => { if (!o) { setDivDialogOpen(false); setDivInviteResult(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{divEditId ? 'Edit Division' : 'Add MPCA Division'}</DialogTitle>
          </DialogHeader>
          {divInviteResult ? (
            <div className="space-y-4" data-testid="division-invite-result">
              <div className="rounded-md border border-emerald-300 bg-emerald-50 p-4">
                <div className="font-semibold text-emerald-900">
                  Division created
                </div>
                <div className="text-sm text-emerald-900/80 mt-1">
                  <strong>No email was sent.</strong> Please share the portal sign-in URL with the secretary
                  through your own channel (phone / WhatsApp / in person). They will sign in using their
                  registered email and the password you set.
                </div>
              </div>
              <div>
                <Label>Portal sign-in URL</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    readOnly
                    value={divInviteResult.portal_url || ''}
                    className="font-mono text-xs"
                    onFocus={(e) => e.target.select()}
                  />
                  <Button type="button" variant="outline" onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(divInviteResult.portal_url);
                      toast.success('Copied');
                    } catch {
                      toast.error('Copy failed');
                    }
                  }}>Copy</Button>
                </div>
              </div>
              <Button onClick={() => { setDivDialogOpen(false); setDivInviteResult(null); }} className="w-full">Done</Button>
            </div>
          ) : (
            <form onSubmit={submitDivision} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5 col-span-2">
                  <Label>Division name *</Label>
                  <Input required value={divForm.name} onChange={(e) => setDivForm({ ...divForm, name: e.target.value })} data-testid="div-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Code * <span className="text-xs text-muted-foreground">(slug)</span></Label>
                  <Input
                    required
                    disabled={!!divEditId}
                    value={divForm.code}
                    onChange={(e) => setDivForm({ ...divForm, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') })}
                    placeholder="INDORE"
                    data-testid="div-code-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>District</Label>
                  <Input value={divForm.district} onChange={(e) => setDivForm({ ...divForm, district: e.target.value })} />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label>Secretary full name *</Label>
                  <Input required value={divForm.secretary_name} onChange={(e) => setDivForm({ ...divForm, secretary_name: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Contact email *</Label>
                  <Input
                    type="email" required
                    disabled={!!divEditId}
                    value={divForm.contact_email}
                    onChange={(e) => setDivForm({ ...divForm, contact_email: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Contact phone</Label>
                  <Input value={divForm.contact_phone} onChange={(e) => setDivForm({ ...divForm, contact_phone: e.target.value })} />
                </div>
                {!divEditId && (
                  <div className="space-y-1.5 col-span-2">
                    <Label>Initial password <span className="text-xs text-muted-foreground">(optional · ≥ 6 chars)</span></Label>
                    <Input
                      type="password"
                      minLength={6}
                      value={divForm.password}
                      onChange={(e) => setDivForm({ ...divForm, password: e.target.value })}
                      placeholder="Enter a password (min 6 chars)"
                      data-testid="div-password-input"
                    />
                  </div>
                )}
              </div>
              {!divEditId && (
                <div className="rounded-md bg-muted/40 border p-2 text-xs text-muted-foreground flex items-start gap-2">
                  <Mail className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>
                    The secretary will sign in with this email + the password you set above. Share the password with them securely.
                  </span>
                </div>
              )}
              <Button type="submit" disabled={divSubmitting} className="w-full" data-testid="div-submit-button">
                {divSubmitting ? 'Saving…' : (divEditId ? 'Save changes' : 'Create division')}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* ───────── Reset Password Dialog ───────── */}
      <Dialog
        open={!!divResetPw}
        onOpenChange={(o) => {
          if (!o) {
            setDivResetPw(null);
            setDivResetReveal(null);
            setDivResetVisible(false);
          }
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-accent" />
              {divResetReveal ? `Password saved · ${divResetPw?.code}` : `Set password for ${divResetPw?.code}`}
            </DialogTitle>
          </DialogHeader>

          {/* After save — show the plaintext once with a copy button. The
              admin already typed/generated it, so they "see" it; this view
              just keeps it on-screen so they can hand it over before
              closing the dialog. The hash on disk is one-way; there's no
              other way to retrieve it later. */}
          {divResetReveal ? (
            <div className="space-y-3">
              <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3">
                <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold mb-1">
                  New password for the secretary
                </div>
                <div className="flex items-center gap-2">
                  <code className="font-mono text-sm flex-1 break-all select-all" data-testid="div-reset-pw-reveal">
                    {divResetReveal}
                  </code>
                  <Button
                    type="button" size="sm" variant="outline"
                    onClick={() => {
                      navigator.clipboard?.writeText(divResetReveal).then(
                        () => toast.success('Password copied to clipboard'),
                        () => toast.error('Copy failed — please select and copy manually'),
                      );
                    }}
                    data-testid="div-reset-pw-copy"
                  >
                    <Copy className="h-3.5 w-3.5 mr-1" /> Copy
                  </Button>
                </div>
              </div>
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2 flex items-start gap-2">
                <ShieldAlert className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>
                  This is the only time this password will be shown. Save or share it with the
                  secretary before closing. (Passwords are stored as a one-way bcrypt hash and
                  can never be retrieved — only reset.)
                </span>
              </div>
              <Button
                type="button"
                className="w-full"
                onClick={() => {
                  setDivResetPw(null);
                  setDivResetReveal(null);
                  setDivResetVisible(false);
                }}
                data-testid="div-reset-pw-close"
              >
                Done
              </Button>
            </div>
          ) : (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const pw = (divResetPw?.password || '').trim();
                if (pw.length < 6) {
                  toast.error('Password must be at least 6 characters');
                  return;
                }
                setDivResetSubmitting(true);
                try {
                  await axios.put(
                    `${API}/divisions/${divResetPw.id}/password`,
                    { password: pw },
                    { withCredentials: true },
                  );
                  toast.success('Password updated — share with the secretary');
                  setDivResetReveal(pw);
                } catch (err) {
                  toast.error(errMsg(err, 'Failed to set password'));
                } finally {
                  setDivResetSubmitting(false);
                }
              }}
              className="space-y-3"
            >
              <div className="space-y-1.5">
                <Label>
                  New password{' '}
                  <span className="text-xs text-muted-foreground">(≥ 6 chars)</span>
                </Label>
                <div className="flex items-center gap-1.5">
                  <div className="relative flex-1">
                    <Input
                      type={divResetVisible ? 'text' : 'password'}
                      minLength={6}
                      autoFocus
                      value={divResetPw?.password || ''}
                      onChange={(e) => setDivResetPw({ ...divResetPw, password: e.target.value })}
                      data-testid="div-reset-pw-input"
                      className="pr-9 font-mono"
                    />
                    <Button
                      type="button" size="icon" variant="ghost"
                      onClick={() => setDivResetVisible((v) => !v)}
                      className="absolute right-0 top-0 h-full w-9 text-muted-foreground hover:text-foreground"
                      title={divResetVisible ? 'Hide password' : 'Show password'}
                      data-testid="div-reset-pw-toggle-visibility"
                    >
                      {divResetVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  <Button
                    type="button" variant="outline" size="sm"
                    onClick={() => {
                      // 12-char password with mixed alphabet+digits — easy
                      // to read aloud over the phone but strong enough.
                      const alpha = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
                      let out = '';
                      const arr = new Uint32Array(12);
                      (window.crypto || window.msCrypto).getRandomValues(arr);
                      for (let i = 0; i < arr.length; i += 1) out += alpha[arr[i] % alpha.length];
                      setDivResetPw({ ...divResetPw, password: out });
                      setDivResetVisible(true);
                    }}
                    data-testid="div-reset-pw-generate"
                    title="Generate a strong random password"
                  >
                    <Wand2 className="h-3.5 w-3.5 mr-1" /> Generate
                  </Button>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                After saving, the password will be displayed on screen one time so you can copy and share it with the secretary.
              </div>
              <Button type="submit" disabled={divResetSubmitting} className="w-full" data-testid="div-reset-pw-submit">
                {divResetSubmitting ? 'Saving…' : 'Save password'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* ───────── Delete Division Dialog ───────── */}
      <Dialog
        open={!!divDelete}
        onOpenChange={(o) => { if (!o && !divDeleteSubmitting) setDivDelete(null); }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <Trash2 className="h-4 w-4" />
              Permanently delete division
            </DialogTitle>
          </DialogHeader>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if ((divDelete?.confirm || '').trim().toUpperCase() !== (divDelete?.code || '').toUpperCase()) {
                toast.error(`Please type the code ${divDelete?.code} to confirm`);
                return;
              }
              setDivDeleteSubmitting(true);
              try {
                const res = await axios.delete(
                  `${API}/divisions/${divDelete.id}`,
                  {
                    params: { confirm_code: divDelete.code },
                    withCredentials: true,
                  },
                );
                const d = res.data?.deleted || {};
                toast.success(
                  `Division ${divDelete.code} deleted — ${d.claims || 0} claim(s), ${(d.attachment_files || 0) + (d.uc_files || 0)} document(s), 1 secretary login removed.`,
                );
                setDivDelete(null);
                fetchDivisions();
              } catch (err) {
                toast.error(errMsg(err, 'Failed to delete division'));
              } finally {
                setDivDeleteSubmitting(false);
              }
            }}
            className="space-y-3"
          >
            <div className="rounded-md bg-rose-50 border border-rose-200 p-3 text-xs text-rose-900 flex items-start gap-2">
              <ShieldAlert className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-semibold mb-1">This will permanently delete:</div>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>The division <strong>{divDelete?.name}</strong> ({divDelete?.code})</li>
                  <li>The linked secretary login (they will lose all access)</li>
                  <li>All grant claims raised by this division</li>
                  <li>All KYC documents &amp; Utilisation Certificates</li>
                </ul>
                <div className="mt-2 font-semibold">This cannot be undone.</div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>
                Type <strong className="font-mono text-rose-700">{divDelete?.code}</strong> to confirm
              </Label>
              <Input
                autoFocus
                value={divDelete?.confirm || ''}
                onChange={(e) => setDivDelete({ ...divDelete, confirm: e.target.value })}
                placeholder={divDelete?.code}
                className="font-mono"
                data-testid="div-delete-confirm-input"
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="button" variant="outline" className="flex-1"
                onClick={() => setDivDelete(null)}
                disabled={divDeleteSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="destructive"
                className="flex-1"
                disabled={
                  divDeleteSubmitting ||
                  (divDelete?.confirm || '').trim().toUpperCase() !== (divDelete?.code || '').toUpperCase()
                }
                data-testid="div-delete-confirm-submit"
              >
                {divDeleteSubmitting ? 'Deleting…' : 'Delete forever'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ───────── Policy Create/Edit Dialog ───────── */}
      <Dialog open={polDialogOpen} onOpenChange={setPolDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScrollText className="h-5 w-5 text-accent" />
              {polEditId ? 'Edit Grant Policy' : 'New Grant Policy'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitPolicy} className="space-y-3">
            <div className="space-y-1.5">
              <Label>Fiscal year *</Label>
              <Input
                required
                disabled={!!polEditId}
                value={polForm.fiscal_year}
                onChange={(e) => setPolForm({ ...polForm, fiscal_year: e.target.value })}
                placeholder="MPL 2026"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Title *</Label>
              <Input
                required
                value={polForm.title}
                onChange={(e) => setPolForm({ ...polForm, title: e.target.value })}
                placeholder="Annual Division Grant FY2026"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Eligible amount per division (₹)</Label>
              <Input
                type="number" min="0" step="1"
                value={polForm.eligible_amount}
                onChange={(e) => setPolForm({ ...polForm, eligible_amount: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Required documents <span className="text-xs text-muted-foreground">(comma-separated)</span></Label>
              <Input
                value={polForm.required_documents}
                onChange={(e) => setPolForm({ ...polForm, required_documents: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea
                rows={4}
                value={polForm.description}
                onChange={(e) => setPolForm({ ...polForm, description: e.target.value })}
                placeholder="What the grant covers, deadlines, conditions, contact for queries…"
              />
            </div>
            <div className="flex items-center justify-between rounded-md bg-muted/30 border p-3">
              <div>
                <Label className="cursor-pointer">Publish to portal</Label>
                <p className="text-xs text-muted-foreground">When off, divisions will not see this policy.</p>
              </div>
              <Switch
                checked={polForm.status === 'active'}
                onCheckedChange={(c) => setPolForm({ ...polForm, status: c ? 'active' : 'closed' })}
              />
            </div>
            <Button type="submit" disabled={polSubmitting} className="w-full" data-testid="policy-submit-button">
              {polSubmitting ? 'Saving…' : (polEditId ? 'Save changes' : 'Create policy')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Divisions;
